import React, { useCallback, useEffect, useState } from "react";
import {
  Mail, ShieldAlert, CheckCircle2, RefreshCw, Loader2,
  Send, AlertCircle, Clock, Server,
} from "lucide-react";
import { toast } from "sonner";
import { adminEmailHealth, adminEmailRefreshDomain } from "../../lib/api";

/**
 * EmailDeliveryBanner — single source of truth for the live/disabled
 * state of the email transport. Renders ONE of three pills:
 *   • LIVE — green, "Live Email Delivery Active · Resend · <from>"
 *   • DISABLED — amber, "Live Email Delivery Disabled — <reason>" (with
 *     a re-check button when the reason is `domain_not_verified`)
 *   • CHECKING — neutral while the initial fetch is in flight.
 *
 * Pass `compact` to render the slim inline variant used on member dossiers.
 */
export const EmailDeliveryBanner = ({ token, userToken = null, compact = false, refreshKey = 0 }) => {
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async () => {
    try {
      const h = await adminEmailHealth(token, userToken);
      setHealth(h);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load email health");
    } finally {
      setLoading(false);
    }
  }, [token, userToken]);

  useEffect(() => { load(); }, [load, refreshKey]);

  const onRecheckDomain = async () => {
    setRefreshing(true);
    try {
      await adminEmailRefreshDomain(token, userToken);
      await load();
      toast.success("Domain status refreshed");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Re-check failed");
    } finally {
      setRefreshing(false);
    }
  };

  if (loading) {
    return (
      <div className="border border-[#454038] bg-[#0F0F0F] p-2 inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-[#A39E93]"
           data-testid="email-banner-loading">
        <Loader2 size={11} className="animate-spin" /> Checking delivery…
      </div>
    );
  }
  if (!health) return null;
  const live = !!health.live;

  if (compact) {
    return (
      <div
        data-testid="email-banner-compact"
        className={"inline-flex items-center gap-1.5 font-mono text-[9.5px] uppercase tracking-[0.25em] border px-2 py-0.5 " +
          (live
            ? "border-[#5DD4B3] text-[#5DD4B3] bg-[#0c1a14]"
            : "border-[#E0A526] text-[#E0A526] bg-[#1a1408]")}
      >
        {live ? <CheckCircle2 size={10} /> : <ShieldAlert size={10} />}
        {live ? "Live · Resend" : "Delivery disabled"}
      </div>
    );
  }

  return (
    <div
      data-testid={live ? "email-banner-live" : "email-banner-disabled"}
      className={"border p-3 flex items-start gap-2 " +
        (live
          ? "border-[#5DD4B3] bg-[#0c1a14] text-[#9ee9c8]"
          : "border-[#E0A526] bg-[#1a1408] text-[#CBB892]")}
    >
      {live ? (
        <CheckCircle2 size={14} className="text-[#5DD4B3] mt-0.5 shrink-0" />
      ) : (
        <ShieldAlert size={14} className="text-[#E0A526] mt-0.5 shrink-0" />
      )}
      <div className="text-[11px] leading-relaxed flex-1 min-w-0">
        <div className="font-mono uppercase tracking-[0.25em] mb-0.5">
          {live ? "✓ LIVE EMAIL DELIVERY ACTIVE" : "⚠ LIVE EMAIL DELIVERY DISABLED"}
        </div>
        <div>
          {live ? (
            <>
              Provider: <span className="text-[#E4DFD3]">Resend</span> · From:{" "}
              <span className="text-[#E4DFD3]">{health.from_addr}</span>
            </>
          ) : (
            <>
              {health.message} Messages are stored only — no email has been sent.
              {health.reason === "domain_not_verified" && (
                <>
                  {" "}Connect Resend to activate delivery once DNS verification completes.
                </>
              )}
            </>
          )}
        </div>
        {!live && health.domain_check_error && (
          <div className="font-mono text-[10px] text-[#A39E93] mt-1">
            {health.domain_check_error}
          </div>
        )}
      </div>
      {!live && (health.reason === "domain_not_verified" || health.reason === "missing_api_key") && (
        <button
          type="button"
          onClick={onRecheckDomain}
          disabled={refreshing}
          data-testid="email-banner-recheck"
          className="inline-flex items-center gap-1 border border-[#E0A526] text-[#E0A526] hover:bg-[#1a1408] px-2 py-1 font-mono text-[9px] uppercase tracking-[0.25em] disabled:opacity-50"
        >
          {refreshing ? <Loader2 size={10} className="animate-spin" /> : <RefreshCw size={10} />}
          Re-check
        </button>
      )}
    </div>
  );
};


/**
 * EmailHealthPanel — full admin dashboard panel. Shown on the Newsletter
 * tab and at the top of the Asset Directory. Lazy-loads its own data.
 */
export const EmailHealthPanel = ({ token, userToken = null }) => {
  const [h, setH] = useState(null);
  const [loading, setLoading] = useState(true);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const d = await adminEmailHealth(token, userToken);
      setH(d);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load email health");
    } finally {
      setLoading(false);
    }
  }, [token, userToken]);

  useEffect(() => { reload(); }, [reload]);

  if (loading || !h) {
    return (
      <div className="border border-[#2D2A26] p-4 font-mono text-xs text-[#757065]"
           data-testid="email-health-loading">
        // Loading email health…
      </div>
    );
  }

  return (
    <div className="border border-[#2D2A26] bg-[#0F0F0F] p-3" data-testid="email-health-panel">
      <div className="flex items-center justify-between gap-2 mb-3">
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#A39E93] inline-flex items-center gap-1.5">
          <Mail size={11} /> Email Health · Provider: <span className="text-[#E4DFD3]">{h.provider?.toUpperCase()}</span>
        </div>
        <button
          type="button"
          onClick={reload}
          data-testid="email-health-refresh"
          className="inline-flex items-center gap-1 border border-[#454038] hover:border-[#E4DFD3] text-[#A39E93] px-2 py-1 font-mono text-[9px] uppercase tracking-[0.25em]"
        >
          <RefreshCw size={10} /> Refresh
        </button>
      </div>

      <EmailDeliveryBanner token={token} userToken={userToken} refreshKey={h?.checked_at} />

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-3">
        <HealthStat label="Emails Today" value={h.emails_today ?? 0} color="#5DD4B3" icon={Send} testid="health-emails-today" />
        <HealthStat label="Failures Today" value={h.failures_today ?? 0} color={h.failures_today ? "#D92D20" : "#A39E93"} icon={AlertCircle} testid="health-failures-today" />
        <HealthStat label="Queue Size" value={h.queue_size ?? 0} color={h.queue_size ? "#E0A526" : "#A39E93"} icon={Server} testid="health-queue-size" />
        <HealthStat label="Avg Delivery" value={h.avg_delivery_seconds != null ? `${h.avg_delivery_seconds}s` : "—"} color="#CBB892" icon={Clock} testid="health-avg-delivery" />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2">
        <HealthLastRow
          label="Last Successful Send"
          row={h.last_successful_send}
          color="#5DD4B3"
          tsKey="delivered_at"
          testid="health-last-success"
        />
        <HealthLastRow
          label="Last Failed Send"
          row={h.last_failed_send}
          color="#D92D20"
          tsKey="failed_at"
          errorText={h.last_failed_send?.last_error}
          testid="health-last-failed"
        />
      </div>

      <div className="font-mono text-[9px] text-[#5a544a] mt-2">
        Individual queue: {h.individual_queue ?? 0} · Newsletter queue: {h.newsletter_queue ?? 0} · Max retry attempts: {h.max_retry_attempts ?? 3}
      </div>
    </div>
  );
};

const HealthStat = ({ label, value, color, icon: Icon, testid }) => (
  <div className="border p-2" style={{ borderColor: `${color}55` }} data-testid={testid}>
    <div className="font-mono text-[8.5px] uppercase tracking-[0.3em] text-[#A39E93] inline-flex items-center gap-1">
      <Icon size={9} /> {label}
    </div>
    <div className="font-display text-2xl mt-0.5" style={{ color }}>{value}</div>
  </div>
);

const HealthLastRow = ({ label, row, color, tsKey, errorText, testid }) => (
  <div className="border border-[#2D2A26] p-2" data-testid={testid}>
    <div className="font-mono text-[8.5px] uppercase tracking-[0.3em] text-[#A39E93]">{label}</div>
    {row ? (
      <div className="font-mono text-[10px] text-[#E4DFD3] mt-0.5 truncate">
        <span style={{ color }}>●</span> {row.subject || "(no subject)"} · to {row.recipient_email}
        <div className="font-mono text-[9px] text-[#5a544a]">
          {(row[tsKey] || "").slice(0, 16).replace("T", " ")}
        </div>
        {errorText && <span className="text-[#E0A526] truncate">{errorText}</span>}
      </div>
    ) : (
      <div className="font-mono text-[10px] text-[#5a544a] mt-0.5">— none yet —</div>
    )}
  </div>
);

export default EmailDeliveryBanner;
